import React, { useEffect } from 'react';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import { saveLink } from '@/data/library';
import Toast from 'react-native-toast-message';
import { redirectToDefault } from '@/utils/constants/linking';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const EMPTY_DRAFT = {};

export default function AddLinkModal() {
    const { id, fromScreen, fromStoreKey } = useLocalSearchParams()
    const storeKey = id || "new"

    const queryClient = useQueryClient()
    const router = useRouter()

    const { data: loggedInUser } = Query.people.loggedInUser()
    const {data: linkToEdit} = Query.links.byId({id: id, options: {enabled: !!id, placeholderData: {}}})
    
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const linkDetails = useEditScreenStore(s => s.AddLinkModal?.[storeKey]) || EMPTY_DRAFT
    
    const saveLinkMutation = useMutation({
        mutationFn: saveLink,
        onSuccess: (data) => {
            removeEditScreenData("AddLinkModal", storeKey)
            Toast.delayShow({type: 'success', text2: 'Link has been saved successfully!'})
            queryClient.invalidateQueries({queryKey: QueryKeys.links.all()});
            
            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined})
            }
            else{
                redirectToDefault(router, "AddLinkModal")
            }
        }
    })


    /*** Effects ***/
    useEffect(() => {
        if(linkToEdit){
            setLinkDetails(linkToEdit)
        }
    }, [linkToEdit]);


    const setLinkDetails = (details) => {
        setEditScreenData("AddLinkModal", storeKey, details)
    }

    return (
        <ModalWrapper 
            loading={saveLinkMutation.isPending} 
            onConfirm={() => saveLinkMutation.mutate({details: {...linkDetails, createdByID: loggedInUser?.id}})} 
            onCancel={() => removeEditScreenData("AddLinkModal", storeKey)}
            title={id ? "Update Link" : "New Link"}
        >

            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" contentContainerStyle={{alignItems:"center"}}>
                <View style={{width:"100%"}}>
                    <InputSection title="Create a Link" description="Add a new link that can be shared thoughout the app. This way you don't have to remember or type out url's every time you need to link an external webpage.">
                        <Input
                            title="Name"
                            maxLength={120}
                            value={linkDetails.name}
                            onUpdate={(new_value) => {
                                setLinkDetails({name: new_value})
                            }}
                        />

                        <Input
                            title="Description"
                            value={linkDetails.description}
                            multiline
                            onUpdate={(new_value) => {
                                setLinkDetails({description: new_value})
                            }}
                        />

                        <Input
                            title="URL"
                            autoCapitalize="none"
                            value={linkDetails.url}
                            onUpdate={(new_value) => {
                                setLinkDetails({url: new_value})
                            }}
                        />
                        
                        <DatePickerInput
                            title="Expiration Date"
                            helpText="An optional date that this link will be considered invalid, and users will be prevented from clicking on it."
                            mode={"datetime"}
                            value={linkDetails.expiration}
                            onUpdate={(new_value) => {
                                setLinkDetails({expiration: new_value})
                            }}
                        />

                    </InputSection>
                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}